export type BundleType = 'starter' | 'connection' | 'legacy';

export type CreditPackType = 'quick' | 'standard' | 'value';

export type CreditTransactionReason =
  | 'signup_bonus'
  | 'bundle_purchase'
  | 'credit_pack_purchase'
  | 'holiday_bonus'
  | 'announcement_published'
  | 'announcement_sent'
  | 'announcement_scheduled'
  | 'admin_adjustment'
  | 'refund';

export interface UserCredits {
  id: string;
  user_id: string;
  announcement_credits: number;
  ai_assist_uses_remaining: number;
  has_purchased_bundle: boolean;
  highest_bundle_purchased: BundleType | null;
  created_at: string;
  updated_at: string;
}

export interface CreditTransaction {
  id: string;
  user_id: string;
  amount: number;
  reason: CreditTransactionReason;
  bundle_type: BundleType | null;
  stripe_session_id: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface BundleConfig {
  id: BundleType;
  name: string;
  tagline: string;
  credits: number;
  price: number;
  priceFormatted: string;
  features: string[];
  featured: boolean;
  unlocksScheduling: boolean;
  unlocksRecurring: boolean;
  unlocksTreslog: boolean;
  unlocksKeepsakes: boolean;
}

export interface CreditPackConfig {
  id: CreditPackType;
  name: string;
  description: string;
  credits: number;
  price: number;
  priceFormatted: string;
  featured?: boolean;
}

export const BUNDLE_CONFIGS: BundleConfig[] = [
  {
    id: 'starter',
    name: 'Starter',
    tagline: 'Keep the Conversation Going',
    credits: 5,
    price: 9,
    priceFormatted: '$9',
    features: [
      '+5 Announcements',
      'Full AI writing assist',
      'AI-generated titles',
      'Remove watermark',
    ],
    featured: false,
    unlocksScheduling: false,
    unlocksRecurring: false,
    unlocksTreslog: false,
    unlocksKeepsakes: false,
  },
  {
    id: 'connection',
    name: 'Connection',
    tagline: 'Show Up When It Counts',
    credits: 10,
    price: 17,
    priceFormatted: '$17',
    features: [
      '+10 Announcements',
      'Everything in Starter',
      'Scheduling & email reminders',
    ],
    featured: true,
    unlocksScheduling: true,
    unlocksRecurring: false,
    unlocksTreslog: false,
    unlocksKeepsakes: false,
  },
  {
    id: 'legacy',
    name: 'Legacy',
    tagline: 'Make It a Practice',
    credits: 25,
    price: 35,
    priceFormatted: '$35',
    features: [
      '+25 Announcements',
      'Everything included',
      'Recurring annual announcements',
      'TRESLOG access',
      'Downloadable keepsakes',
    ],
    featured: false,
    unlocksScheduling: true,
    unlocksRecurring: true,
    unlocksTreslog: true,
    unlocksKeepsakes: true,
  },
];

export const CREDIT_PACK_CONFIGS: CreditPackConfig[] = [
  {
    id: 'quick',
    name: 'Quick Top-Up',
    description: 'A few more moments',
    credits: 5,
    price: 7,
    priceFormatted: '$7',
    featured: false,
  },
  {
    id: 'standard',
    name: 'Standard Pack',
    description: 'Regular sharing',
    credits: 10,
    price: 12,
    priceFormatted: '$12',
    featured: false,
  },
  {
    id: 'value',
    name: 'Value Pack',
    description: 'Building a practice',
    credits: 25,
    price: 25,
    priceFormatted: '$25',
    featured: true,
  },
];

// Feature access helpers
export function canUseScheduling(bundle: BundleType | null): boolean {
  if (!bundle) return false;
  return bundle === 'connection' || bundle === 'legacy';
}

export function canUseRecurring(bundle: BundleType | null): boolean {
  if (!bundle) return false;
  return bundle === 'legacy';
}

export function hasWatermark(hasPurchased: boolean): boolean {
  return !hasPurchased;
}
